import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { JobCard } from '../../config';

// Reopening the job card modal after a round trip to the invoice builder.
//
// The builder is a full-screen route, so opening an invoice from the modal
// unmounts the host page and the modal with it. The modal therefore describes
// itself in the navigation state on the way out (`reopenJobCard`), the builder
// hands that back untouched when it closes, and the host page reopens the
// modal from it on mount.
//
// The snapshot is the entity and the header text, never the row the host
// opened the modal from: a paged host (Fees) may not have that row loaded
// again on return. Given an explicit projectId the modal hydrates the rest
// from job_cards_list_view itself.

export type JobCardModalTab =
  | 'overview'
  | 'project'
  | 'timesheets'
  | 'invoices'
  | 'fees';

export interface JobCardModalRestore {
  projectId: number;
  stageId: number | null;
  tab: JobCardModalTab;
  projectName: string;
  jobNumber: string;
  /** The entity's workflow status, which drives the Overview's won-fee rule. */
  workflowStatus?: string | null;
}

/** Navigation state a host page may arrive with. */
export interface JobCardReturnState {
  reopenJobCard?: JobCardModalRestore;
}

/** Header-only JobCard for a restored modal; the modal hydrates the rest. */
export const jobCardFromRestore = (restore: JobCardModalRestore): JobCard => ({
  project_id: restore.projectId,
  parent_project_id: restore.projectId,
  stage_row_id: restore.stageId,
  project_name: restore.projectName,
  job_number: restore.jobNumber,
  workflow_status: restore.workflowStatus ?? undefined,
  project_lead_name: '',
  client_name: '',
  allocations: [],
  first_allocation_date: '',
  last_allocation_date: '',
  total_hours: 0,
  total_cost: 0,
  invoices: null,
  total_invoiced: 0,
  invoice_count: 0,
});

/**
 * The modal to reopen on this page load, if the user has just come back from
 * the invoice builder. Captured once, then cleared from the history entry —
 * location.state survives a refresh, and without the clear every reload of
 * the page would pop the modal open again.
 */
function useJobCardModalRestore(): JobCardModalRestore | null {
  const location = useLocation();
  const navigate = useNavigate();
  const [restore] = useState<JobCardModalRestore | null>(
    () => (location.state as JobCardReturnState | null)?.reopenJobCard ?? null,
  );

  useEffect(() => {
    if (!restore) return;
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    // Once, on arrival: later URL changes (filters) have nothing to clear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restore]);

  return restore;
}

/** The props a restored modal is rendered with. */
export interface RestoredJobCardModalProps {
  isOpen: true;
  jobCard: JobCard;
  projectId: number;
  stageId: number | null;
  initialTab: JobCardModalTab;
}

/**
 * Everything a host page needs to reopen the modal after the invoice builder:
 * `restoredProps` to spread onto <JobCardModal> in place of the page's own
 * (null when there is nothing to restore), and `clearRestored` to call when
 * the modal closes or the user opens another row. One hook rather than the
 * state + effect + prop fallbacks each host would otherwise repeat — the three
 * hosts open the modal from three different row shapes, and that is the only
 * part that should differ between them.
 */
export function useRestoredJobCardModal(): {
  restoredProps: RestoredJobCardModalProps | null;
  clearRestored: () => void;
} {
  const arrived = useJobCardModalRestore();
  const [restored, setRestored] = useState<JobCardModalRestore | null>(arrived);
  const clearRestored = useCallback(() => setRestored(null), []);
  const restoredProps = useMemo<RestoredJobCardModalProps | null>(
    () =>
      restored
        ? {
            isOpen: true,
            jobCard: jobCardFromRestore(restored),
            projectId: restored.projectId,
            stageId: restored.stageId,
            initialTab: restored.tab,
          }
        : null,
    [restored],
  );
  return { restoredProps, clearRestored };
}
